import React, { useState, useEffect, useCallback } from 'react';
import { Upload, CheckCircle, XCircle, AlertCircle, FileText, Shield, Image, Code, Sparkles } from 'lucide-react';

const ComplianceChecker = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [report, setReport] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingForPayment, setUploadingForPayment] = useState(false);
  const [analysisStep, setAnalysisStep] = useState('');
  const [analysisProgress, setAnalysisProgress] = useState(0);

  // Backend API URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const verifyAndAnalyze = useCallback(async (sessionId) => {
    setAnalyzing(true);
    setError(null);
    setAnalysisStep('Connecting to server...');
    setAnalysisProgress(0);

    try {
      // Use EventSource for Server-Sent Events
      const eventSource = new EventSource(`${API_URL}/verify-and-analyze/${sessionId}`);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.status === 'progress') {
            setAnalysisStep(data.step);
            setAnalysisProgress(data.progress);
          } else if (data.status === 'complete') {
            // Transform backend response to match frontend format
            setReport({
              appName: data.result.app_info.name,
              bundleId: data.result.app_info.bundle_id,
              version: data.result.app_info.version,
              buildNumber: data.result.app_info.build,
              summary: {
                score: data.result.compliance_score,
                critical: data.result.summary.critical,
                warning: data.result.summary.warning,
                info: data.result.summary.info,
                passed: data.result.summary.passed || 0,
                total: data.result.summary.total_issues,
              },
              issues: data.result.issues,
              passedChecks: data.result.passed_checks || [],
            });
            
            eventSource.close();
            setAnalyzing(false);
            window.history.replaceState({}, document.title, window.location.pathname);
          } else if (data.status === 'error') {
            setError(data.error);
            eventSource.close();
            setAnalyzing(false);
          }
        } catch (parseError) {
          console.error('Error parsing SSE data:', parseError);
        }
      };

      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        setError('Connection lost during analysis. Please try again.');
        eventSource.close();
        setAnalyzing(false);
      };

      // Cleanup function
      return () => {
        eventSource.close();
      };

    } catch (error) {
      console.error('Error verifying payment:', error);
      setError(error.message || 'Failed to verify payment and analyze IPA');
      setAnalyzing(false);
    }
  }, [API_URL]);

  // Check for payment success on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && window.location.pathname.includes('payment-success')) {
      verifyAndAnalyze(sessionId);
    }
  }, [verifyAndAnalyze]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (uploadedFile) => {
    if (uploadedFile.name.endsWith('.ipa')) {
      setError(null);
      createCheckoutSession(uploadedFile);
    } else {
      setError('Please upload a .ipa file');
    }
  };

  const createCheckoutSession = async (ipaFile) => {
    setUploadingForPayment(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', ipaFile);

      const response = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success || !data.checkout_url) {
        throw new Error('Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;

    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError(error.message || 'Failed to initiate payment. Make sure the backend is running.');
      setUploadingForPayment(false);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Info.plist':
        return <FileText className="w-5 h-5" />;
      case 'Assets':
        return <Image className="w-5 h-5" />;
      case 'Binary Analysis':
        return <Code className="w-5 h-5" />;
      case 'Privacy':
        return <Shield className="w-5 h-5" />;
      case 'Metadata':
        return <Sparkles className="w-5 h-5" />;
      case 'AI Analysis':
        return <Sparkles className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };

  if (report) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">iOS PreCheck</span>
              </div>
              <button
                onClick={() => { setReport(null); setError(null); }}
                className="text-blue-600 font-semibold hover:text-blue-700 transition-colors"
              >
                Analyze Another App
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Compliance Report</h1>
            <p className="text-gray-600">Detailed analysis of your iOS app</p>
          </div>

          {/* Summary Card */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">App Name</p>
                <p className="text-xl font-semibold text-gray-900">{report.appName}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Bundle ID</p>
                <p className="text-lg font-mono text-gray-900">{report.bundleId}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Version</p>
                <p className="text-lg font-semibold text-gray-900">{report.version} ({report.buildNumber})</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Compliance Score</p>
                <p className={`text-4xl font-bold ${getScoreColor(report.summary.score)}`}>
                  {report.summary.score}/100
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div className="text-center p-4 rounded-xl bg-red-50">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <span className="text-3xl font-bold text-red-600">{report.summary.critical}</span>
                </div>
                <p className="text-sm font-medium text-gray-600">Critical Issues</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-yellow-50">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <AlertCircle className="w-6 h-6 text-yellow-500" />
                  <span className="text-3xl font-bold text-yellow-600">{report.summary.warning}</span>
                </div>
                <p className="text-sm font-medium text-gray-600">Warnings</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-green-50">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  <span className="text-3xl font-bold text-green-600">{report.summary.passed || 0}</span>
                </div>
                <p className="text-sm font-medium text-gray-600">Passed Checks</p>
              </div>
            </div>
          </div>

          {/* Issues List */}
          {report.issues.length > 0 && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Issues Found</h2>
              <div className="space-y-4 mb-12">
                {report.issues.map((issue, index) => (
                  <div
                    key={index}
                    className={`bg-white rounded-xl shadow-md border-l-4 p-6 hover:shadow-lg transition-shadow ${getSeverityColor(issue.severity)}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        {getSeverityIcon(issue.severity)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          {getCategoryIcon(issue.category)}
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                            {issue.category}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-3">
                          {issue.title}
                        </h3>
                        <p className="text-gray-700 mb-4 leading-relaxed">{issue.description}</p>
                        <div className="bg-gradient-to-r from-gray-50 to-white rounded-lg p-4 border border-gray-200">
                          <p className="text-sm font-semibold text-gray-900 mb-2">How to fix:</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{issue.fix}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Passed Checks */}
          {report.passedChecks && report.passedChecks.length > 0 && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <CheckCircle className="w-7 h-7 text-green-500" />
                Passed Checks ({report.passedChecks.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                {report.passedChecks.map((check, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-md border-l-4 border-green-200 p-5 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {getCategoryIcon(check.category)}
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                            {check.category}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 mb-1">
                          {check.title}
                        </h4>
                        <p className="text-sm text-gray-600">{check.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* All Clear Message */}
          {report.issues.length === 0 && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg border-2 border-green-200 p-12 text-center mb-12">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">All Clear!</h3>
              <p className="text-lg text-gray-600">No compliance issues detected. Your app looks good to submit!</p>
            </div>
          )}

          {/* Backend Analysis Note */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Code className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Real Binary Analysis</h3>
                <p className="text-gray-700 leading-relaxed">
                  This analysis uses actual binary inspection to detect private API usage - 
                  the same issues that cause App Store rejections days after submission. This goes beyond 
                  Apple's automated upload checks and catches problems before human reviewers see them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">iOS PreCheck</span>
            </div>
            <a href="/" className="text-blue-600 font-semibold hover:text-blue-700 transition-colors">Back to Home</a>
          </div>
        </div>
      </header>

      <div className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-extrabold text-gray-900 mb-4">
              iOS App Store <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Compliance Checker</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Upload your .ipa file to check for App Store rejection reasons
            </p>
            <p className="text-sm text-gray-500 mt-3">
              $2.99 per scan - Powered by real binary analysis
            </p>
          </div>

          {error && (
            <div className="bg-white rounded-xl border-2 border-red-200 p-6 mb-8 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-red-900 mb-1">Error</p>
                  <p className="text-sm text-red-700">{error}</p>
                  {error.includes('localhost:8000') && (
                    <p className="text-xs text-red-600 mt-3 bg-red-50 p-3 rounded-lg">
                      Make sure the backend is running: <code className="bg-red-100 px-2 py-1 rounded font-mono">python3 main.py</code>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div
            className={`bg-white rounded-2xl shadow-2xl p-12 border-2 border-dashed transition-all ${
              dragActive ? 'border-blue-500 bg-blue-50 scale-105' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {analyzing || uploadingForPayment ? (
              <div className="text-center">
                <div className="relative w-20 h-20 mx-auto mb-6">
                  <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
                  <div className="absolute inset-0 border-4 border-t-blue-600 rounded-full animate-spin"></div>
                </div>

                {analyzing && (
                  <>
                    {/* Progress bar */}
                    <div className="w-full max-w-md mx-auto mb-4">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 transition-all duration-300"
                          style={{ width: `${analysisProgress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <p className="text-xl font-bold text-gray-900 mb-2">
                      {analysisStep || 'Analyzing your app...'}
                    </p>
                    <p className="text-sm text-gray-600">{analysisProgress}% complete</p>
                  </>
                )}

                {uploadingForPayment && (
                  <>
                    <p className="text-xl font-bold text-gray-900 mb-2">Preparing checkout...</p>
                    <p className="text-sm text-gray-600">Uploading your .ipa file securely</p>
                  </>
                )}
              </div>
            ) : (
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center">
                  <Upload className="w-10 h-10 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-2">
                  Drop your .ipa file here
                </p>
                <p className="text-gray-600 mb-4">or click to browse</p>
                <p className="text-sm text-gray-500 mb-8">
                  You'll be redirected to Stripe to complete payment ($2.99)
                </p>
                <input
                  type="file"
                  accept=".ipa"
                  onChange={handleChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 cursor-pointer transition-all"
                >
                  Select .ipa File - $2.99
                </label>
              </div>
            )}
          </div>

          <div className="mt-8 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">What we check:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Private API detection</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Info.plist validation</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Privacy permissions</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Asset requirements</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Binary analysis</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">Metadata compliance</span>
              </div>
            </div>
          </div>

          {/* Security Note */}
          <div className="mt-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-gray-900">Secure Payment:</span> Your payment is processed securely by Stripe. Your .ipa file is analyzed immediately after payment and then permanently deleted from our servers.
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Questions? Email support@iosprecheck.com
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceChecker;